/*
 * HELP.TSX - HELP CENTER AND TUTORIAL VIDEOS PAGE
 * 
 * A dedicated help center page that displays tutorial videos in a responsive grid layout.
 * 
 * Key Features:
 * - Grid-based display of tutorial video cards with clickable thumbnails
 * - YouTube video thumbnails with play button overlay
 * - Links open videos in new YouTube tabs
 * - Responsive grid layout that adapts to chat panel visibility
 * - Smooth transitions and hover effects for enhanced user experience
 * - Fixed header with scrollable content area
 * 
 * Responsive Grid System:
 * - Tutorial video grid automatically adjusts column count based on chat panel state
 * - Uses getResponsiveGridClassesWithTransitions for smooth transitions
 * - Maintains optimal viewing experience across different screen sizes
 * - Chat-aware layout prevents video overcrowding when chat is open
 * 
 * Video Integration:
 * - Displays YouTube video thumbnails instead of embedded iframes
 * - Robust YouTube video ID extraction supporting multiple URL formats (youtu.be, youtube.com/watch, youtube.com/embed)
 * - Clickable thumbnails with play button overlay
 * - Opens videos in new YouTube tabs when clicked
 * - Maintains aspect ratio and responsive containers
 * 
 * User Experience:
 * - Fixed header with help information stays visible during scrolling
 * - Smooth hover animations on video cards with play button highlight
 * - Scrollable content area for optimal space utilization
 * - Clear "Watch on YouTube" links for accessibility
 * 
 * Recent Changes:
 * - Changed from iframe embeds to thumbnail + link approach due to YouTube embedding restrictions
 * - Added YouTube thumbnail images with play button overlay
 * - Implemented fallback for thumbnail loading errors
 * - Enhanced hover effects for better user interaction
 * - Adopted the shared page layout so the shell owns outer vertical spacing.
 */

import React from 'react';
import PageLayout from '../components/patterns/PageLayout';
import { THEME_CLASSES } from '../constants/themeConstants';
import { useChatContext } from '../context/ChatContext';
import { getResponsiveGridClassesWithTransitions } from '../utils/responsiveUtils';

interface TutorialVideo {
  title: string;
  description: string;
  videoUrl: string;
}

// Import tutorial videos data
import tutorialVideosData from '../data/tutorialVideos.json';

const Help: React.FC = () => {
  const { isChatVisible } = useChatContext();
  const tutorialVideos: TutorialVideo[] = tutorialVideosData.tutorialVideos;

  const VideoCard: React.FC<TutorialVideo> = ({ title, description, videoUrl }) => {
    // Extract video ID from YouTube URL (supports both youtu.be and youtube.com formats)
    const extractVideoId = (url: string): string => {
      try {
        // Handle youtu.be/VIDEO_ID format
        if (url.includes('youtu.be/')) {
          return url.split('youtu.be/')[1].split('?')[0];
        }
        // Handle youtube.com/watch?v=VIDEO_ID format
        if (url.includes('youtube.com/watch')) {
          const urlParams = new URL(url).searchParams;
          return urlParams.get('v') || '';
        }
        // Handle youtube.com/embed/VIDEO_ID format
        if (url.includes('youtube.com/embed/')) {
          return url.split('youtube.com/embed/')[1].split('?')[0];
        }
        // Fallback: try to get last part of path
        return url.split('/').pop()?.split('?')[0] || '';
      } catch (error) {
        console.error('Error extracting video ID from URL:', url, error);
        return '';
      }
    };

    const videoId = extractVideoId(videoUrl);
    const thumbnailUrl = videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : '';
    const watchUrl = videoId ? `https://www.youtube.com/watch?v=${videoId}` : videoUrl;

    return (
      <div className={`rounded-lg overflow-hidden shadow-md ${THEME_CLASSES.card} transition-transform hover:scale-105`}>
        <a
          href={watchUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="block relative w-full h-48 bg-gray-200 dark:bg-gray-700 group"
        >
          {thumbnailUrl && (
            <img
              src={thumbnailUrl}
              alt={title}
              className="w-full h-full object-cover"
              onError={(e) => {
                // Fallback to standard quality thumbnail if maxres fails
                const target = e.target as HTMLImageElement;
                if (!target.src.includes('hqdefault')) {
                  target.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
                }
              }}
            />
          )}
          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center">
            <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center opacity-90 group-hover:opacity-100 group-hover:scale-110 transition-all">
              <svg className="w-8 h-8 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
          </div>
        </a>
        <div className="p-4">
          <h3 className={`font-bold text-lg mb-2 ${THEME_CLASSES.text.primary}`}>{title}</h3>
          <p className={`text-sm ${THEME_CLASSES.text.secondary} mb-3`}>{description}</p>
          <a
            href={watchUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex items-center text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors`}
          >
            Watch on YouTube
            <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>
      </div>
    );
  }; return (
    <PageLayout className="overflow-hidden">
      {/* Fixed Header */}
      <header className="space-y-4 px-4">
        <div className="flex items-center justify-between">
          <h1 className={`text-2xl font-bold ${THEME_CLASSES.text.primary}`}>Help & Tutorials</h1>
        </div>
        <p className={THEME_CLASSES.text.secondary}>
          Welcome to the rlpCRM help center. Watch these short tutorial videos to learn how to use the system effectively.
        </p>
      </header>

      {/* Scrollable container for cards */}
      <div className="flex-1 overflow-y-auto pb-8">
        <div className="px-4">
          <div className={`${getResponsiveGridClassesWithTransitions('notes', isChatVisible)} gap-6`}>
            {tutorialVideos.map((video, index) => (
              <VideoCard key={index} {...video} />
            ))}
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default Help;
